import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { v, Checkbox, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, COTENANT_SUFFIX } from '../../../parties/index.js';
/**
 * Besittningsbrytande grunder (12 kap. 57 § JB). `text` is the verbatim
 * clause shown in the document; `short` is the editor-facing option label;
 * `field` is the dependent freetext key (null = no freetext).
 */
const GROUNDS = [
    {
        value: 'misskotsel',
        short: 'Åsidosatt förpliktelser (misskötsel)',
        text: 'Som hyresgäst har du åsidosatt dina förpliktelser i så hög grad att det inte skäligen kan fordras att hyresförhållandet förlängs:',
        field: 'ground_misskotsel_desc',
        fieldLabel: 'Beskrivning (måste fyllas i)',
    },
    {
        value: 'rivning',
        short: 'Rivning + ersättningslokal',
        text: 'Huset ska rivas och hyresvärden anvisar därför följande godtagbar(-a) ersättningslokal(-er):',
        field: 'ground_rivning_lokaler',
        fieldLabel: 'Ersättningslokal(-er)',
    },
    {
        value: 'ombyggnad',
        short: 'Ombyggnad + ersättningslokal',
        text: 'Huset ska genomgå en sådan ombyggnad att du som hyresgäst uppenbarligen inte kan sitta kvar i lokalen utan nämnvärd olägenhet för genomförandet av ombyggnaden. Hyresvärden anvisar följande godtagbar(-a) ersättningslokal(-er):',
        field: 'ground_ombyggnad_lokaler',
        fieldLabel: 'Ersättningslokal(-er)',
    },
    {
        value: 'ombyggnad_avbojt',
        short: 'Ombyggnad — avböjd ersättningslokal',
        text: 'Huset ska genomgå en sådan ombyggnad att du som hyresgäst uppenbarligen inte kan sitta kvar i lokalen utan nämnvärd olägenhet för genomförandet av ombyggnaden, men att du som hyresgäst utan befogad anledning avböjt en godtagbar ersättningslokal som hyresvärden har anvisat.',
        field: null,
        fieldLabel: '',
    },
    {
        value: 'befogad_anledning',
        short: 'Befogad anledning (lokalen behövs)',
        text: 'Hyresvärden har befogad anledning att upplösa hyresförhållandet eftersom lokalen behövs för:',
        field: 'ground_befogad_desc',
        fieldLabel: 'Skäl (egen verksamhet / omdisponering / omstrukturering / annat)',
    },
    {
        value: 'sakligt_skal',
        short: 'Sakligt skäl (fritext)',
        text: 'Hyresvärden har sakligt skäl för uppsägningen, nämligen:',
        field: 'ground_sakligt_desc',
        fieldLabel: 'Sakligt skäl',
    },
];
// English one-line summaries for the `grounds` multi_select options
// (keyed by GROUNDS value, which doubles as the option `code`).
const GROUND_SUMMARIES = {
    misskotsel: 'Tenant has neglected obligations so seriously that extension cannot reasonably be required.',
    rivning: 'The building is to be demolished and the landlord offers an acceptable replacement premises.',
    ombyggnad: 'The building is to undergo major rebuilding and the landlord offers an acceptable replacement premises.',
    ombyggnad_avbojt: 'Major rebuilding, but the tenant declined an acceptable replacement premises without valid reason.',
    befogad_anledning: 'The landlord has justified cause to end the tenancy because the premises are needed for a stated use.',
    sakligt_skal: 'The landlord has another objectively valid reason for the termination (free text).',
};
export const metadata = {
    sections: [
        { ref: 'parter', title: 'Parter' },
        { ref: 'uppsagning', title: 'Uppsägning till hyresgästen' },
        { ref: 'meddelande', title: 'Meddelande' },
        { ref: 'underskrift', title: 'Underskrift (hyresvärd)' },
        { ref: 'erkannande', title: 'Erkännande (hyresgäst)' },
    ],
    templateKey: 'uppsagning_avflyttning_lokal',
    version: 'v1',
    category: 'Lokalhyra',
    title: 'Uppsägning av lokalhyresavtal för avflyttning',
    shortTitle: 'Uppsägning avflyttning (lokal)',
    titleSuffix: { key: 'contract_number', prefix: 'avtal' },
    formNumber: 'Formulär R-25',
    preamble: 'Hyresvärdens uppsägning av lokalhyresavtalet för avflyttning enligt 12 kap. jordabalken.',
    description: 'Hyresvärdens uppsägning av ett lokalhyresavtal för avflyttning (12 kap. JB). Använd när hyresvärden vill att lokalhyresgästen flyttar och vägrar förlängning på en besittningsbrytande grund (12 kap. 57 §).\n\n' +
        'Vem undertecknar: hyresvärden (uppsägningen) och hyresgästen (erkännande om mottagande). Skickas via Scrive.\n\n' +
        'OBS! Uppsägningen måste vara delgiven hyresgästen senast sista dagen för uppsägning — säkerställ att delgivning kan styrkas. Hyresgästen har indirekt besittningsskydd och måste hänskjuta tvisten till hyresnämnden för medling inom två månader, annars förfaller rätten till ersättning (12 kap. 58 § JB).\n\n' +
        'Kopplad mall — skapas under det lokalhyresavtal som sägs upp. Parter, lokal och avtalsnummer förförifylls från lokalavtalet (även om det är ett importerat avtal).',
    status: 'active',
    signingMode: 'scrive',
    connectedTo: ['lokalhyra'],
    signingPlacementAnchors: 'per-party-label',
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [{ key: 'contract_number', fromParentKey: 'contract_number' }],
    },
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om lokalavtalet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065) — editor fields only; body renders parties
    // under role labels (Hyresvärd/Hyresgäst), so it is not PartyBlock-driven.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärd',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'Hyresgäst',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_org_number' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'Hyresgäst',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_org_number' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    {
        key: 'contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        section: 'Hyreskontrakt',
        requiredAtFinal: true,
        help: 'Numret på det lokalhyresavtal som sägs upp.',
        scopes: { copy: 'never' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    // Parter — generated from metadata.partyProjection (ADR-0065). The
    // document body renders these under role labels; the projection drives
    // the editor fields + autofill.
    ...expandPartyProjection(metadata),
    // Lokalen
    {
        key: 'property_address',
        label: 'Fastighetsadress',
        type: 'textarea',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'Lokalen',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    // Uppsägning
    {
        key: 'termination_date',
        label: 'Upphörande den',
        type: 'date',
        section: 'Uppsägning',
        requiredAtFinal: true,
        help: 'Datum då hyresavtalet upphör att gälla.',
        scopes: { copy: 'manual' },
    },
    // Grund för uppsägningen
    {
        key: 'grounds',
        label: 'Anledning till att förlängning vägras',
        type: 'multi_select',
        section: 'Grund för uppsägningen',
        requiredAtFinal: true,
        options: GROUNDS.map((g) => ({
            value: g.value,
            label: g.short,
            code: g.value,
            summary: GROUND_SUMMARIES[g.value],
        })),
        help: '"Ombyggnad — avböjd ersättningslokal" används endast om hyresgästen redan anvisats en godtagbar lokal.',
    },
    {
        key: 'ground_misskotsel_desc',
        label: 'Åsidosatt förpliktelser — beskrivning',
        type: 'textarea',
        section: 'Grund för uppsägningen',
        visibleWhen: { grounds: 'misskotsel' },
        requiredWhen: { grounds: 'misskotsel' },
    },
    {
        key: 'ground_rivning_lokaler',
        label: 'Rivning — ersättningslokal(-er)',
        type: 'textarea',
        section: 'Grund för uppsägningen',
        visibleWhen: { grounds: 'rivning' },
        help: 'En lokal per rad. Lägg till fler vid behov.',
    },
    {
        key: 'ground_ombyggnad_lokaler',
        label: 'Ombyggnad — ersättningslokal(-er)',
        type: 'textarea',
        section: 'Grund för uppsägningen',
        visibleWhen: { grounds: 'ombyggnad' },
        help: 'En lokal per rad. Lägg till fler vid behov.',
    },
    {
        key: 'ground_befogad_desc',
        label: 'Befogad anledning — skäl',
        type: 'textarea',
        section: 'Grund för uppsägningen',
        visibleWhen: { grounds: 'befogad_anledning' },
        requiredWhen: { grounds: 'befogad_anledning' },
        help: 'T.ex. egen verksamhet, omdisponering eller omstrukturering av fastighetens lokalbestånd.',
    },
    {
        key: 'ground_sakligt_desc',
        label: 'Sakligt skäl',
        type: 'textarea',
        section: 'Grund för uppsägningen',
        visibleWhen: { grounds: 'sakligt_skal' },
        requiredWhen: { grounds: 'sakligt_skal' },
    },
    // Display-only editor note (type: 'note') — never read by the Body, never
    // rendered into the document. Påminnelse till hyresvärden om delgivning.
    {
        key: 'delgivning_note',
        label: 'Observera – delgivning',
        type: 'note',
        section: 'Grund för uppsägningen',
        help: 'Uppsägningen måste vara delgiven hyresgästen senast sista dagen för uppsägning. Säkerställ att delgivning kan styrkas.',
    },
];
export const sampleValues = {
    contract_number: 'L-3087',
    property_designation: 'Verkstaden 4',
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    tenant_name: 'Café Söder HB',
    tenant_org_number: '969712-5678',
    cotenant_name: '',
    cotenant_org_number: '',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    property_municipality: 'Stockholm',
    termination_date: '2026-12-31',
    grounds: ['befogad_anledning'],
    ground_misskotsel_desc: '',
    ground_rivning_lokaler: '',
    ground_ombyggnad_lokaler: '',
    ground_befogad_desc: 'omstrukturering av fastighetens lokalbestånd — lokalerna ska slås samman till en större butikslokal.',
    ground_sakligt_desc: '',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
const groundText = (values, field) => field
    ? v(values[field])
    : '';
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    const grounds = Array.isArray(values.grounds) ? values.grounds : [];
    const showCotenant = cond(mode, !!values.cotenant_name);
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-3 text-[12.5px] font-semibold", children: "Upps\u00E4gning av lokalhyresavtal f\u00F6r avflyttning" }), _jsx(Section, { sectionRef: "parter", title: "Parter", children: _jsxs("div", { className: "border border-stone-300", children: [_jsx(FieldRow, { label: "Hyreskontrakt nr", children: v(values.contract_number) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Hyresg\u00E4st", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.tenant_org_number) }), showCotenant && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Hyresg\u00E4st (medhyresg\u00E4st)", children: v(values.cotenant_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.cotenant_org_number) })] }))] }) }), _jsx(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning till hyresg\u00E4sten", children: _jsxs("div", { className: "border border-stone-300", children: [_jsx(FieldRow, { label: "Fastighetsadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Upph\u00F6rande den", children: v(values.termination_date) })] }) }), _jsxs(Section, { sectionRef: "meddelande", title: "Meddelande", children: [_jsxs("p", { className: "font-semibold", children: ["Hyresv\u00E4rden s\u00E4ger upp hyresavtalet f\u00F6r avflyttning till den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.termination_date) }), "."] }), _jsx("p", { className: "mt-3 font-medium", children: "Anledningen till att f\u00F6rl\u00E4ngning v\u00E4gras \u00E4r f\u00F6ljande:" }), GROUNDS.map((g) => {
                        const checked = cond(mode, grounds.includes(g.value));
                        return (_jsxs("div", { className: "mt-2", children: [_jsxs("p", { children: [_jsx(Checkbox, { checked: checked }), g.text] }), g.field && checked && (_jsx("div", { className: "mt-1 ml-5 min-h-[1.5rem] whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: groundText(values, g.field) }))] }, g.value));
                    }), _jsx("p", { className: "mt-4", children: "Om ni inte accepterar att hyresf\u00F6rh\u00E5llandet upph\u00F6r har ni att inom tv\u00E5 m\u00E5nader fr\u00E5n denna upps\u00E4gning h\u00E4nskjuta tvisten till hyresn\u00E4mnden f\u00F6r medling enligt 12 kap. 58 \u00A7 jordabalken. I annat fall f\u00F6rfaller r\u00E4tten till ers\u00E4ttning med anledning av upps\u00E4gningen." })] }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrift", title: "Underskrift (hyresv\u00E4rd)", children: _jsx(SigningBlock, { parties: [{ label: 'Hyresvärd', role: 'lessor', ordinal: 0 }], signatories: signatories }) }) }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "erkannande", title: "Erk\u00E4nnande (hyresg\u00E4st)", children: [_jsx("p", { className: "mb-4", children: "En med denna likalydande handling har jag denna dag tagit emot." }), _jsx(SigningBlock, { parties: showCotenant
                                ? [
                                    { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                                    { label: 'Medhyresgäst', role: 'lessee', ordinal: 1 },
                                ]
                                : [{ label: 'Hyresgäst', role: 'lessee', ordinal: 0 }], signatories: signatories })] }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map